import { lastMinuteDealsGradient } from '@/app/common/components/Promotions/styled';
import { TextContainer } from '@/app/common/components/Promotions/LastMinuteDealsBadge/styled';
import { FlashSalesCounterSlim } from '@/app/common/components/Promotions/FlashSalesCounter';
import { createFlashSalesConfigByExpirationDate } from '@/app/common/components/Promotions/FlashSalesCounter/utils';
import { useFetchRooms } from '@/app/components/hotel-detail/hooks/useFetchRooms';
import { useHotelPromotion } from '@/app/components/hotel-detail/hooks/useFetchHotelPromotions';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { Icon } from '@/app/common/icons/Icon';
import { flashSalesIconSize } from '@/app/common/icons/promotionIcons';
import { css } from 'styled-components';
import useIsClient from '../search/hooks/useIsClient';
import { useTranslation } from '../translations/Translation';

export function LmnDealHeader(
  props: { expirationDate?: string; badge?: boolean } & React.HTMLAttributes<HTMLDivElement>
) {
  const { expirationDate, badge, ...rest } = props;
  const { t } = useTranslation();
  const isClient = useIsClient();
  const { offersData } = useOffersContext();
  const { data: roomsData } = useFetchRooms();
  const { localization } = usePageWrapperData();
  const { imageBasePath } = localization;

  const { showLmnDealsPromotion } = useHotelPromotion();
  const showNewLmnDealHeader = offersData?.hasFlashSaleOffers && showLmnDealsPromotion;
  const headerIconSize = (slot: number) => (showNewLmnDealHeader ? slot : flashSalesIconSize(slot));

  if (!(roomsData as RoomsServiceResponseType)?.anyFlashSales) return null;

  const icon = `${imageBasePath}icons/last_minute_deals_underline_white.svg`;
  return (
    <div
      css={`
        display: flex;
        align-items: center;
        position: relative;
        gap: 4px;
        padding: ${showNewLmnDealHeader ? '12px 16px' : '8px 16px'};
        color: white;
        background: ${showNewLmnDealHeader ? lastMinuteDealsGradient : customCss.gradients.flashSaleDeal};

        @media ${customCss.breakpoints.lg} {
          padding: ${showNewLmnDealHeader ? '16px 24px' : '8px 24px'};
        }

        ${
          !showNewLmnDealHeader
            ? `
          &::before {
            content: '';
            background-image: url('${imageBasePath}deal-pattern.svg');
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            opacity: 0.1;          
          }
        `
            : undefined
        }
      `}
      {...rest}
    >
      {/* Requested at the larger rendered size; CSS scales it down below lg. */}
      <Icon
        category="Brands and products"
        name={showNewLmnDealHeader ? 'icon-lastminute-deals' : 'icon-flash-sales'}
        size={headerIconSize(32)}
        color="iconOnDark"
        alt="lastminute deal"
        css={`
          width: ${headerIconSize(24)}px;
          height: ${headerIconSize(24)}px;
          margin: ${(24 - headerIconSize(24)) / 2}px;
          @media ${customCss.breakpoints.lg} {
            width: ${headerIconSize(32)}px;
            height: ${headerIconSize(32)}px;
            margin: ${(32 - headerIconSize(32)) / 2}px;
          }
        `}
      />
      {showNewLmnDealHeader ? (
        <TextContainer
          dangerouslySetInnerHTML={{ __html: t('vacation.oss.last_minute_deals.label_HTML') }}
          $icon={icon}
          $color="#FFFFFF"
          $preset={css`
            ${({ theme }) => theme.kallisto.mixins.font('Title5')};

            @media ${customCss.breakpoints.lg} {
              ${({ theme }) => theme.kallisto.mixins.font('Title4')};
            }
          `}
          className="lmndeal-header-text-overlay"
        />
      ) : (
        <div
          css={css`
            flex-grow: 1;
            grid-area: 1/2/1/3;
            color: ${({ theme }) => theme.kallisto.color.text.static.textOnDark};
            ${({ theme }) => theme.kallisto.mixins.font('Title5')};

            @media ${customCss.breakpoints.lg} {
              ${({ theme }) => theme.kallisto.mixins.font('Title4')};
            }
          `}
        >
          {t('vacation.select.flashsales.banner.title')}
        </div>
      )}
      {isClient && expirationDate ? (
        <FlashSalesCounterSlim
          flashSalesConfig={createFlashSalesConfigByExpirationDate(expirationDate, t)}
          css={`
            margin-left: auto;
            ${badge ? 'border-radius:0 8px 8px 0' : undefined};
          `}
        />
      ) : null}
    </div>
  );
}
